import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PublicHeader, PublicFooter, Loading } from "@/components/shared";

/** Minimal markdown → HTML for our legal drafts (# ## ** - lists, paragraphs). */
export function markdownToHtml(md) {
  if (!md) return "";
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const out = [];
  let inUl = false;
  let para = [];

  const flushPara = () => {
    if (!para.length) return;
    const text = para.join(" ").trim();
    if (text) out.push(`<p>${inline(text)}</p>`);
    para = [];
  };
  const closeUl = () => {
    if (inUl) {
      out.push("</ul>");
      inUl = false;
    }
  };
  const inline = (s) =>
    s
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>");

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) {
      flushPara();
      closeUl();
      continue;
    }
    if (line.startsWith("---")) {
      flushPara();
      closeUl();
      out.push("<hr />");
      continue;
    }
    const h = /^(#{1,3})\s+(.+)$/.exec(line.trim());
    if (h) {
      flushPara();
      closeUl();
      const level = h[1].length;
      out.push(`<h${level}>${inline(h[2])}</h${level}>`);
      continue;
    }
    if (/^[-*]\s+/.test(line.trim())) {
      flushPara();
      if (!inUl) {
        out.push("<ul>");
        inUl = true;
      }
      out.push(`<li>${inline(line.trim().replace(/^[-*]\s+/, ""))}</li>`);
      continue;
    }
    closeUl();
    para.push(line.trim());
  }
  flushPara();
  closeUl();
  return out.join("\n");
}

export default function LegalDocument({ title, src, testId }) {
  const [html, setHtml] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(src)
      .then((r) => {
        if (!r.ok) throw new Error("load failed");
        return r.text();
      })
      .then((md) => {
        if (!cancelled) setHtml(markdownToHtml(md));
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [src]);

  return (
    <div className="min-h-screen" data-testid={testId}>
      <PublicHeader />
      <main className="mx-auto max-w-3xl px-4 py-12">
        <p className="mb-6 text-sm text-muted-foreground">
          <Link to="/" className="underline hover:text-foreground">
            Home
          </Link>
          {" · "}
          <Link to="/terms" className="underline hover:text-foreground">
            Terms
          </Link>
          {" · "}
          <Link to="/privacy" className="underline hover:text-foreground">
            Privacy
          </Link>
        </p>
        {error ? (
          <p className="text-muted-foreground">Couldn&apos;t load {title}. Please try again later.</p>
        ) : html === null ? (
          <Loading label={`Loading ${title}…`} />
        ) : (
          <article
            className="legal-prose space-y-4 text-[15px] leading-relaxed text-foreground [&_h1]:text-3xl [&_h1]:font-black [&_h1]:tracking-tight [&_h2]:mt-8 [&_h2]:text-xl [&_h2]:font-bold [&_h3]:mt-6 [&_h3]:text-lg [&_h3]:font-semibold [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-5 [&_hr]:my-8 [&_hr]:border-border [&_p]:text-foreground/90"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        )}
      </main>
      <PublicFooter />
    </div>
  );
}

export function TermsPage() {
  return <LegalDocument title="Terms of Service" src="/legal/terms.md" testId="terms-page" />;
}

export function PrivacyPage() {
  return <LegalDocument title="Privacy Policy" src="/legal/privacy.md" testId="privacy-page" />;
}
